import React, { useEffect, useState } from "react";
import Api from "../Api";
import "./SecretProducts.css";

const SECRET_PATH = "/x9a7-secret-ops/";
const ITEMS_PER_PAGE = 20;

export default function SecretProducts() {
  const [products, setProducts] = useState([]);
  const [name, setName] = useState("");
  const [imei, setImei] = useState("");
  const [price, setPrice] = useState("");
  const [description, setDescription] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const [modalOpen, setModalOpen] = useState(false); // sale confirm modal
  const [editModalOpen, setEditModalOpen] = useState(false); // edit modal
  const [addedModalOpen, setAddedModalOpen] = useState(false);
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [confirming, setConfirming] = useState(false);

  // edit form state
  const [editName, setEditName] = useState("");
  const [editImei, setEditImei] = useState(""); // IMEI editable
  const [editPrice, setEditPrice] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);

  const formatPrice = (value) => {
    if (!value && value !== 0) return "₦0.00";
    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: "NGN",
    }).format(value);
  };

  const loadProducts = async () => {
    try {
      const res = await Api.get(`${SECRET_PATH}records/`);
      const sorted = res.data.records.sort((a, b) =>
        a.name.toLowerCase().localeCompare(b.name.toLowerCase())
      );
      setProducts(sorted);
    } catch (err) {
      console.error("Failed to load products", err);
    }
  };

  const onImeiChange = (value) => {
    const cleaned = value.replace(/\s+/g, ""); // removes all spaces
    setImei(cleaned);
  };

  const addProduct = async () => {
    const cleanName = (name || "").trim();
    const cleanImei = (imei || "").replace(/\s+/g, "");

    if (!cleanName || !cleanImei) {
      return alert("Name and IMEI/Serial required (no spaces allowed)");
    }

    try {
      const check = await Api.post(`${SECRET_PATH}check-imei/`, {
        imei_or_serial: cleanImei,
      });

      if (check.data.exists) {
        setDuplicateModalOpen(true);
        return;
      }

      await Api.post(`${SECRET_PATH}add/`, {
        name: cleanName,
        imei_or_serial: cleanImei,
        price,
        description,
      });

      setName("");
      setImei("");
      setPrice("");
      setDescription("");
      setAddedModalOpen(true);
      await loadProducts();
      setCurrentPage(1);
    } catch (err) {
      console.error(err);
      if (err.response?.data?.error === "duplicate") {
        setDuplicateModalOpen(true);
      } else {
        alert("Failed to add product");
      }
    }
  };

  const openSaleModal = (product) => {
    setSelectedProduct(product);
    setModalOpen(true);
  };

  // Open edit modal only for unsold products
  const openEditModal = (product) => {
    if (product.is_sold) return; // safety: do nothing if sold
    setSelectedProduct(product);
    setEditName(product.name || "");
    setEditImei(product.imei_or_serial || "");
    setEditPrice(product.price !== undefined && product.price !== null ? product.price : "");
    setEditDescription(product.description || "");
    setEditModalOpen(true);
  };

  const confirmSold = async () => {
    if (!selectedProduct) return;
    setConfirming(true);

    try {
      await Api.post(`${SECRET_PATH}mark-sold/${selectedProduct.id}/`);
      setModalOpen(false);
      setConfirming(false);
      setSelectedProduct(null);
      await loadProducts();
    } catch (err) {
      console.error(err);
      setConfirming(false);
      alert("Failed to mark as sold");
    }
  };

  const saveEdit = async () => {
    if (!selectedProduct) return;
    const cleanName = (editName || "").trim();
    const cleanImei = (editImei || "").replace(/\s+/g, "");

    if (!cleanName || !cleanImei) {
      return alert("Name and IMEI/Serial required (no spaces allowed)");
    }

    setSavingEdit(true);
    try {
      await Api.post(`${SECRET_PATH}update/${selectedProduct.id}/`, {
        name: cleanName,
        imei_or_serial: cleanImei,
        price: editPrice,
        description: editDescription,
      });

      setSavingEdit(false);
      setEditModalOpen(false);
      setSelectedProduct(null);
      await loadProducts();
    } catch (err) {
      console.error("Failed to save edits", err);
      setSavingEdit(false);
      if (err.response?.data?.error === "duplicate") {
        alert("This IMEI/Serial already exists.");
      } else {
        alert("Failed to save changes");
      }
    }
  };

  const filteredProducts = products
    .filter((p) => {
      const search = (searchTerm || "").toLowerCase().replace(/\s+/g, "");
      const nameMatch = (p.name || "").toLowerCase().includes(search);
      const imeiSource = p.cleaned_imei !== undefined ? p.cleaned_imei : (p.imei_or_serial || "");
      const imeiMatch = imeiSource.toLowerCase().replace(/\s+/g, "").includes(search);
      return nameMatch || imeiMatch;
    })
    .sort((a, b) => a.name.localeCompare(b.name));

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, products.length]);

  useEffect(() => {
    loadProducts();
  }, []);

  const totalItems = filteredProducts.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / ITEMS_PER_PAGE));
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const pageItems = filteredProducts.slice(startIndex, endIndex);

  const goToPage = (page) => {
    const p = Math.max(1, Math.min(totalPages, page));
    setCurrentPage(p);
    const top = document.querySelector(".product-list");
    if (top) top.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const prevPage = () => goToPage(currentPage - 1);
  const nextPage = () => goToPage(currentPage + 1);
  const pages = Array.from({ length: totalPages }, (_, i) => i + 1);

  return (
    <div className="secret-dashboard">
      <h1>Secret Product Dashboard</h1>

      {/* Add Product Form */}
      <div className="add-product-form">
        <h2>Add Secret Product</h2>
        <input placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} />
        <input placeholder="IMEI/Serial" value={imei} onChange={(e) => onImeiChange(e.target.value)} />
        <input placeholder="Price" value={price} onChange={(e) => setPrice(e.target.value)} />
        <input placeholder="Description (optional)" value={description} onChange={(e) => setDescription(e.target.value)} />
        <button onClick={addProduct}>Add Product</button>
      </div>

      <hr />

      {/* Search */}
      <div className="search-box">
        <input placeholder="Search products by name or IMEI..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
      </div>

      {/* Product List */}
      <h2>All Secret Products</h2>
      <div className="list-meta">
        <div>
          Showing {totalItems === 0 ? 0 : startIndex + 1} - {Math.min(endIndex, totalItems)} of {totalItems}
        </div>
      </div>

      <div className="product-list">
        {pageItems.map((p) => {
          const isSold = !!p.is_sold;
          // only attach click/keyboard handlers when NOT sold
          const cardProps = isSold
            ? { role: undefined, tabIndex: undefined, onClick: undefined, onKeyDown: undefined, style: { cursor: "default" } }
            : {
                role: "button",
                tabIndex: 0,
                onClick: () => openEditModal(p),
                onKeyDown: (e) => { if (e.key === "Enter") openEditModal(p); },
                style: { cursor: "pointer" }
              };

          return (
            <div
              key={p.id}
              className={`product-card ${isSold ? "sold non-clickable" : ""}`}
              {...cardProps}
            >
              <h3>{p.name}</h3>
              <p className="imei"><strong>IMEI/Serial:</strong> {p.imei_or_serial}</p>
              <p className="price"><strong>Price:</strong> {formatPrice(p.price)}</p>
              <p className="desc">{p.description || "No description"}</p>
              <p className="sold-on">
                <strong>Sold On:</strong>{" "}
                {p.date_sold ? new Date(p.date_sold).toLocaleString("en-US", { timeZone: "Africa/Lagos", hour12: true, month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "numeric" }) : "Not sold"}
              </p>

              {!isSold && (
                <button
                  className="sold-btn"
                  onClick={(e) => { e.stopPropagation(); openSaleModal(p); }}
                  disabled={confirming && selectedProduct?.id === p.id}
                >
                  {confirming && selectedProduct?.id === p.id ? "Processing..." : "Mark as Sold"}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination-wrap">
          <button className="page-btn" onClick={prevPage} disabled={currentPage === 1}>Prev</button>
          <div className="page-numbers">
            {pages.map((pg) => (
              <button key={pg} className={`page-btn ${pg === currentPage ? "active" : ""}`} onClick={() => goToPage(pg)}>{pg}</button>
            ))}
          </div>
          <button className="page-btn" onClick={nextPage} disabled={currentPage === totalPages}>Next</button>
        </div>
      )}

      {/* Edit Modal */}
      {editModalOpen && selectedProduct && (
        <div className="modal-overlay" onClick={() => setEditModalOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Edit Product</h3>

            <label>Name</label>
            <input value={editName} onChange={(e) => setEditName(e.target.value)} />

            <label>IMEI/Serial</label>
            <input value={editImei} onChange={(e) => setEditImei(e.target.value.replace(/\s+/g, ""))} />

            <label>Price</label>
            <input value={editPrice} onChange={(e) => setEditPrice(e.target.value)} />

            <label>Description</label>
            <input value={editDescription} onChange={(e) => setEditDescription(e.target.value)} />

            <div className="modal-buttons">
              <button onClick={() => { setEditModalOpen(false); setSelectedProduct(null); }} disabled={savingEdit}>Cancel</button>
              <button onClick={saveEdit} disabled={savingEdit}>{savingEdit ? "Saving..." : "Save"}</button>
            </div>
          </div>
        </div>
      )}

      {/* Sold Modal */}
      {modalOpen && selectedProduct && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Confirm Sale</h3>
            <p>Are you sure you want to mark "{selectedProduct.name}" as sold?</p>
            <div className="modal-buttons">
              <button onClick={() => setModalOpen(false)} disabled={confirming}>Cancel</button>
              <button onClick={confirmSold} disabled={confirming}>{confirming ? "Processing..." : "Confirm"}</button>
            </div>
          </div> 
        </div>
      )}

      {/* Duplicate IMEI Modal */}
      {duplicateModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Duplicate IMEI</h3>
            <p>This IMEI/Serial number already exists in the system.</p>
            <button onClick={() => setDuplicateModalOpen(false)}>OK</button>
          </div>
        </div>
      )}

      {/* Added Modal */}
      {addedModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Product Added</h3>
            <p>The product has been added successfully!</p>
            <button onClick={() => setAddedModalOpen(false)}>OK</button>
          </div>
        </div>
      )}
    </div>
  );
}
